import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { endorsersAtom, signReportAtom } from '@/interfaces/report';

interface SignDialogProps {
  onClose: (reason?: string) => void;
  reportId: string;
  reportName: string;
}

export default function SignDialog({ onClose, reportId, reportName }: SignDialogProps) {
  const { data: endorsers } = useAtomValue(endorsersAtom);
  const { mutate, isPending, data: result } = useAtomValue(signReportAtom);

  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    if (result === undefined) return;
    if (result === 'OK') {
      onClose('updated');
    } else if (result === 'INVALID_PASSWORD') {
      window.alert(t('Signup.message.passwordNotSame'));
    } else if (result === 'UNKNOWN') {
      window.alert(t('Common.message.unknownErrorOccurred'));
    } else {
      window.alert(t('Common.message.serverErrorOccurred'));
    }
  }, [onClose, result, t]);

  function onSignClick() {
    mutate({ reportId, userId, password });
  }

  return (
    <Stack gap={2} sx={{ minWidth: 600, p: 3 }}>
      <DialogTitle sx={{ px: 0 }}>{t('FDA.label.sign')}</DialogTitle>
      <Typography>
        {t('Common.label.name')}: {reportName}
      </Typography>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('FDA.label.endorser')}</TableCell>
              <TableCell>{t('FDA.label.endorsement')}</TableCell>
              <TableCell>{t('Common.label.status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {endorsers?.map((endorser) => (
              <TableRow key={endorser.user.email}>
                <TableCell>{endorser.user.email}</TableCell>
                <TableCell>{endorser.endorsement.name}</TableCell>
                <TableCell>{t(`FDA.label.${endorser.status}`)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <FormControl>
        <InputLabel>{t('FDA.label.endorser')}</InputLabel>
        <Select label={t('FDA.label.endorser')} value={userId} onChange={(e) => setUserId(e.target.value)}>
          {endorsers?.map((e) => (
            <MenuItem key={e.user.id} value={e.user.id}>
              {e.user.email}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        type="password"
        label={t('Signup.label.password')}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />

      <Stack direction="row" justifyContent="center" gap={3}>
        <Button variant="contained" onClick={() => onClose()} sx={{ bgcolor: '#CCC', color: 'black' }}>
          {t('Common.label.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={userId === '' || password.length === 0 || isPending}
          onClick={onSignClick}
          sx={{ bgcolor: 'info.main' }}
        >
          {t('FDA.label.sign')}
        </Button>
      </Stack>
    </Stack>
  );
}
